// The admin side of web push: can this browser do it, ask permission, subscribe, unsubscribe. The server (src/push.js) does the sending.
const SW_URL = '/admin-sw.js';
const SCOPE = '/admin';

/** { ok, reason }: whether this browser can receive push notifications, and if not, what to tell the admin. */
export function pushSupport() {
  if (typeof window === 'undefined') return { ok: false, reason: '' };
  if (!window.isSecureContext) return { ok: false, reason: 'Notifications need a secure (https) connection.' };
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = Boolean(window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone);
  if (ios && !standalone) return { ok: false, reason: 'On iPhone and iPad, first add this admin to your Home Screen (Share, then Add to Home Screen) and open it from there.' };
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return { ok: false, reason: 'This browser cannot receive notifications.' };
  return { ok: true, reason: '' };
}

export async function currentSubscription() {
  try { const reg = await navigator.serviceWorker.getRegistration(SCOPE); return (await reg?.pushManager.getSubscription()) || null; }
  catch { return null; }
}

const keyBytes = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
};

// A freshly registered worker is "installing" for a moment: wait until it is active before subscribing.
function whenActive(reg) {
  if (reg.active) return Promise.resolve(reg);
  return new Promise((resolve, reject) => {
    const sw = reg.installing || reg.waiting;
    if (!sw) return reject(new Error('The notification service could not start.'));
    const check = () => { if (sw.state === 'activated') resolve(reg); else if (sw.state === 'redundant') reject(new Error('The notification service could not start.')); };
    sw.addEventListener('statechange', check);
    check();
  });
}

const sameKey = (sub, publicKey) => {
  const have = sub.options?.applicationServerKey;
  if (!have) return true;                                  // cannot tell: assume it is fine
  const a = new Uint8Array(have), b = keyBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
};

/**
 * What to tell the admin when the browser's push service refuses to register. Chrome and Edge say "Registration failed - push service error" for
 * several different reasons and cannot say which; these are the real ones, most likely first.
 */
async function explain(err) {
  const brave = Boolean(navigator.brave && (await navigator.brave.isBrave?.().catch(() => false)));
  const detail = `${err?.name || 'Error'}: ${err?.message || err}`;
  if (brave) return new Error(`Brave turns push notifications off by default. Open brave://settings/privacy, turn on "Use Google Services for Push Messaging", restart Brave and try again. (${detail})`);
  if (err?.name === 'NotAllowedError') return new Error(`The browser blocked the request. Allow notifications for this site in the address bar's site settings, then try again. (${detail})`);
  if (err?.name === 'AbortError' || /push service/i.test(err?.message || '')) {
    return new Error(`This browser could not reach its push service, so it could not sign this device up. That is almost always a setting or the network rather than the site: ` +
      `turn off any VPN, proxy or ad/tracker blocker for this site; check that a firewall or antivirus is not blocking Google (Chrome) or Microsoft (Edge) push services; ` +
      `use a normal window, not a private one; and check your computer's date and time are right. Then try again, or turn it on from your phone instead. (${detail})`);
  }
  return new Error(`Could not turn notifications on. (${detail})`);
}

/** Must be called from a click: browsers only show the permission prompt in answer to one. Returns the subscription as plain JSON. */
export async function subscribePush(publicKey) {
  if (Notification.permission === 'denied') throw new Error("Notifications are blocked for this site. Allow them in your browser's site settings, then try again.");
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notifications were not allowed, so nothing was turned on.');
  try {
    const reg = await whenActive(await navigator.serviceWorker.register(SW_URL, { scope: SCOPE }));
    let sub = await reg.pushManager.getSubscription();
    if (sub && !sameKey(sub, publicKey)) { await sub.unsubscribe().catch(() => {}); sub = null; }       // made against an older server key: start over
    if (!sub) {
      const options = { userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) };
      try { sub = await reg.pushManager.subscribe(options); }
      catch (err) {
        if (err?.name !== 'InvalidStateError') throw err;
        await (await reg.pushManager.getSubscription())?.unsubscribe().catch(() => {});
        sub = await reg.pushManager.subscribe(options);
      }
    }
    return sub.toJSON();
  } catch (err) {
    throw await explain(err);
  }
}

export async function unsubscribePush() {
  const sub = await currentSubscription();
  const endpoint = sub?.endpoint || '';
  try { await sub?.unsubscribe(); } catch { /* the server forgetting it is what matters */ }
  return endpoint;
}
